/* Global notification bell — fixed top-right on every authed page.
   Click opens a dropdown with the most recent items + unread badge + Mark all read + View All. */

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, CheckCheck, FileCheck, AlertTriangle, CreditCard, Info,
  Archive, Trash2, ExternalLink, Loader2,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import "./NotificationBell.css";

const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const iconFor = (n) => {
  const map = {
    "file-check": FileCheck, "alert-triangle": AlertTriangle,
    "credit-card": CreditCard, "info": Info, "loader": Loader2,
  };
  return map[n.icon] || (n.kind === "billing" ? CreditCard : n.kind === "system" ? Info : FileCheck);
};

export default function NotificationBell() {
  const nav = useNavigate();
  const { items, unread, markRead, markAllRead, archive } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const off = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);

  const openItem = async (n) => {
    if (!n.read_at) await markRead(n.id);
    if (n.action_url) nav(n.action_url);
    setOpen(false);
  };

  const recent = items.slice(0, 10);

  return (
    <div className="nb" ref={wrapRef}>
      <button
        className={`nb-btn ${unread > 0 ? "has-unread" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications, ${unread} unread`}
        data-testid="notification-bell"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="nb-badge" data-testid="notification-bell-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="nb-dropdown" data-testid="notification-bell-dropdown">
          <div className="nb-head">
            <b>Notifications</b>
            {unread > 0 && (
              <button className="nb-mark-all" onClick={markAllRead} data-testid="notification-mark-all">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          {recent.length === 0 ? (
            <div className="nb-empty" data-testid="notification-bell-empty">
              You&#39;re all caught up.
            </div>
          ) : (
            <ul className="nb-list">
              {recent.map((n) => {
                const Icon = iconFor(n);
                return (
                  <li
                    key={n.id}
                    className={`nb-item p-${n.priority} ${n.read_at ? "read" : "unread"}`}
                    onClick={() => openItem(n)}
                    data-testid={`notif-${n.id}`}
                  >
                    <span className="nb-i"><Icon size={14} /></span>
                    <div className="nb-body">
                      <div className="nb-title">{n.title}</div>
                      {n.body && <div className="nb-blurb">{n.body}</div>}
                      <div className="nb-meta">
                        <span>{relativeTime(n.created_at)}</span>
                        {n.action_url && <span><ExternalLink size={10} /> Open</span>}
                      </div>
                    </div>
                    <button
                      className="nb-x"
                      onClick={(e) => { e.stopPropagation(); archive(n.id); }}
                      aria-label="Archive"
                      title="Archive"
                      data-testid={`notif-archive-${n.id}`}
                    >
                      <Archive size={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            className="nb-viewall"
            onClick={() => { setOpen(false); nav("/notifications"); }}
            data-testid="notification-view-all"
          >
            View All Notifications →
          </button>
        </div>
      )}
    </div>
  );
}
